import type { HomeMonthRuns } from '../../types/api';

interface PipelineCardProps {
  runs: HomeMonthRuns | null;
  isLoading: boolean;
  error: string | null;
}

/** Bar and legend order; the modifier picks the status colour token. */
const SEGMENTS: readonly { key: keyof HomeMonthRuns; label: string }[] = [
  { key: 'needs_you', label: 'Needs you' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'approved', label: 'Approved' },
  { key: 'failed', label: 'Failed' },
  { key: 'closed', label: 'Closed' },
];

/**
 * This month's runs as one part-to-whole bar. The legend lists every bucket
 * with its count, so the bar is never the only way to read a number, and the
 * bar's label says the same in words.
 */
export function PipelineCard({ runs, isLoading, error }: PipelineCardProps) {
  const total = runs ? SEGMENTS.reduce((sum, s) => sum + runs[s.key], 0) : 0;

  return (
    <section className="home-panel pipeline-card" aria-labelledby="pipeline-title">
      <div className="home-panel__head">
        <h2 className="home-panel__title" id="pipeline-title">
          Progress
        </h2>
        {runs ? (
          <span className="home-panel__aside">
            {total} {total === 1 ? 'post' : 'posts'} this month
          </span>
        ) : null}
      </div>

      {isLoading ? (
        <p className="kb-section__status" role="status">
          Counting this month's posts
        </p>
      ) : error || !runs ? (
        <p className="home-panel__error">
          <span role="alert">Could not count this month's posts.</span>
        </p>
      ) : total === 0 ? (
        <p className="home-panel__empty">No posts yet this month.</p>
      ) : (
        <>
          <div
            className="pipeline-bar"
            role="img"
            aria-label={SEGMENTS.map((s) => `${s.label} ${runs[s.key]}`).join(', ')}
          >
            {SEGMENTS.filter((s) => runs[s.key] > 0).map((s) => (
              <span
                key={s.key}
                className={`pipeline-bar__segment pipeline-swatch--${s.key}`}
                style={{ flexGrow: runs[s.key] }}
                title={`${s.label}: ${runs[s.key]}`}
              />
            ))}
          </div>
          <ul className="pipeline-legend">
            {SEGMENTS.map((s) => (
              <li className="pipeline-legend__item" key={s.key}>
                <span className={`pipeline-legend__swatch pipeline-swatch--${s.key}`} />
                <span className="pipeline-legend__label">{s.label}</span>
                <span className="pipeline-legend__count">{runs[s.key]}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="pipeline-card__stages">
        Research → Write → Caption check → Fact check → Your review → Design → Design check → Your
        approval. We pause for you at both reviews.
      </p>
    </section>
  );
}
